import $ from "jquery";
import * as _ from "underscore";

type DeviceTypeMap = { [key: string]: () => boolean };

const browserTypes: DeviceTypeMap = {
  chrome: () => {
    return window.chrome && /chrome/i.test(navigator.userAgent);
  },
  safari: () => {
    return (
      /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
    );
  },
  firefox: () => /Firefox\//.test(navigator.userAgent),
  ie: () => /Windows.*Trident/.test(navigator.userAgent),
};

/**
 * Identify the kind of machine, can be either OS or more specific device type (e.g. iPhone / iPad)
 */
const platformTypes: DeviceTypeMap = {
  iPhone: () => /iPhone/.test(navigator.userAgent),
  iPad: () => /iPad/.test(navigator.userAgent),
  android: () => /android/i.test(navigator.userAgent),
  mac: () => /Mac/.test(navigator.userAgent),
  windows: () => /Win/.test(navigator.userAgent),
  linux: () => /Linux/.test(navigator.userAgent),
};

const allDeviceTypes: DeviceTypeMap = {
  ...browserTypes,
  ...platformTypes,
  iOS: () => /iPad|iPhone|iPod/.test(navigator.userAgent),
  mobile: () => {
    return is("iOS") || is("android");
  },
};

export const is = function (deviceName: string): boolean {
  const deviceChecker = allDeviceTypes[deviceName];

  if (!deviceChecker) {
    throw Error(`Device "${deviceName}" not recognised`);
  }

  return deviceChecker();
};

export const info = function () {
  const result: Record<keyof DeviceTypeMap, boolean> = {};
  _.each(_.keys(allDeviceTypes), function (deviceName) {
    if (is(deviceName)) {
      result[deviceName] = true;
    }
  });
  return result;
};

export const isSmall = function () {
  return $(window).width() <= 546;
};

export const getSizeCategory = () => {
  const width = window.innerWidth;

  // These values should match the media queries in _constants.scss
  if (width <= 546) {
    return "mobile";
  } else if (width <= 754) {
    return "tablet";
  } else if (width <= 850) {
    return "mediumDesktop";
  }
  return "desktop";
};

/** From https://stackoverflow.com/a/4819886 */
export const isTouchDevice = () => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator["msMaxTouchPoints"] > 0
  );
};

/**
 * Returns `true` if running as a home-screen app on iOS or Android.
 */
export const isStandalone = _.memoize(() => {
  // on iOS Safari
  if (window.navigator?.["standalone"]) {
    return true;
  }

  // on Android Chrome
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) {
    return true;
  }

  return false;
});

export const deviceString = () => {
  /** The first platform which matches in platformTypes */
  let platformType: string;

  for (const platformName in platformTypes) {
    if (platformTypes[platformName]()) {
      platformType = platformName;
      break;
    }
  }

  /** The first browser which matches in browserTypes */
  let browserType: string;

  for (const browserName in browserTypes) {
    if (browserTypes[browserName]()) {
      browserType = browserName;
      break;
    }
  }

  const outputList = [platformType, browserType].filter(
    (typeString) => typeString
  );

  return outputList.length > 0 ? outputList.join(", ") : "unknown";
};
